/**
 * Authentication service for Duo integration
 */
import axios from 'axios'
import { buildApiUrl } from '../config/api.js'

class AuthService {
  constructor() {
    this.isAuthenticated = false
    this.user = null
    this.listeners = []
  }

  /**
   * Add authentication state change listener
   */
  addAuthListener(callback) {
    this.listeners.push(callback)
  }

  /**
   * Remove authentication state change listener
   */
  removeAuthListener(callback) {
    const index = this.listeners.indexOf(callback)
    if (index > -1) {
      this.listeners.splice(index, 1)
    }
  }

  /**
   * Notify all listeners of authentication state change
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      callback(this.isAuthenticated, this.user)
    })
  }

  /**
   * Check authentication status
   */
  async checkAuthStatus() {
    try {
      // Get session token from localStorage or user data
      const sessionToken = this.user?.session_token || localStorage.getItem('session_token')
      
      const response = await axios.get(buildApiUrl('/api/auth/status'), {
        withCredentials: true,
        headers: sessionToken ? {
          'Authorization': `Bearer ${sessionToken}`
        } : {}
      })
      
      if (response.data.authenticated) {
        this.isAuthenticated = true
        this.user = response.data.user || response.data
        // Store session token for future requests
        if (response.data?.session_token) {
          localStorage.setItem('session_token', response.data.session_token)
        }
        this.notifyListeners()
        return true
      } else {
        this.isAuthenticated = false
        this.user = null
        localStorage.removeItem('session_token')
        this.notifyListeners()
        return false
      }
    } catch (error) {
      console.error('Auth status check failed:', error)
      this.isAuthenticated = false
      this.user = null
      localStorage.removeItem('session_token')
      this.notifyListeners()
      return false
    }
  }

  /**
   * Handle SSO callback
   */
  async handleSSOCallback(code, state) {
    try {
      const response = await axios.post(
        buildApiUrl('/api/auth/sso/callback'),
        { code, state },
        { withCredentials: true }
      )

      if (response.data.success) {
        this.isAuthenticated = true
        this.user = response.data.data
        
        // Store session token
        if (response.data.data?.session_token) {
          localStorage.setItem('session_token', response.data.data.session_token)
        }
        
        this.notifyListeners()
        return {
          success: true,
          user: response.data.data
        }
      } else {
        return {
          success: false,
          message: response.data.message
        }
      }
    } catch (error) {
      console.error('SSO callback failed:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'SSO callback failed'
      }
    }
  }

  /**
   * Logout
   */
  async logout() {
    try {
      // Get session token from localStorage or user data
      const sessionToken = this.user?.session_token || localStorage.getItem('session_token')
      
      // Clean up S3 data before logout
      if (sessionToken) {
        try {
          await axios.post(buildApiUrl('/api/process/cleanup'), {
            session_token: sessionToken
          }, {
            withCredentials: true
          })
          console.log('✅ S3 data cleaned up on logout')
        } catch (cleanupError) {
          console.warn('Cleanup request failed (non-critical):', cleanupError)
        }
      }
      
      await axios.post(buildApiUrl('/api/auth/logout'), {
        session_token: sessionToken
      }, {
        withCredentials: true
      })
    } catch (error) {
      console.error('Logout request failed:', error)
    } finally {
      this.isAuthenticated = false
      this.user = null
      // Clear session token from localStorage
      localStorage.removeItem('session_token')
      this.notifyListeners()
    }
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.user
  }

  /**
   * Check if authenticated
   */
  isUserAuthenticated() {
    return this.isAuthenticated
  }

  /**
   * Check if SSO is enabled
   */
  async checkSSOEnabled() {
    try {
      const response = await axios.get(buildApiUrl('/api/auth/sso/enabled'), {
        withCredentials: true
      })
      return response.data.success && response.data.enabled
    } catch (error) {
      console.error('SSO check failed:', error)
      return false
    }
  }

  /**
   * Get SSO authorization URL
   */
  async getSSOUrl(username) {
    try {
      const response = await axios.get(buildApiUrl('/api/auth/sso/url'), {
        withCredentials: true,
        params: { username }
      })
      
      if (response.data.success) {
        return {
          success: true,
          url: response.data.url
        }
      } else {
        return {
          success: false,
          message: response.data.message
        }
      }
    } catch (error) {
      console.error('SSO URL failed:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'SSO URL failed'
      }
    }
  }
}

// Create singleton instance
const authService = new AuthService()

export default authService
